import { useEffect, useState, useMemo } from "react";
import { Box, Button, Grid, Typography, CircularProgress } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import axiosBase from '../../common/axiosBase';
import coreConfig from '../../common/coreConfig';
import { useNavigate } from "react-router-dom";
import EquipmentCard from "./EquipmentCard";
import { Responsive, WidthProvider } from "react-grid-layout/legacy";
import "react-grid-layout/css/styles.css";
import Stack from '@mui/material/Stack';
import { logger } from "../../App";
const ResponsiveGridLayout = WidthProvider(Responsive);

// Utility function to compact layout
const compactLayout = (layout, cols = 3) => {
  // Sort by y position first, then x position
  const sortedLayout = [...layout].sort((a, b) => {
    if (a.y === b.y) {
      return a.x - b.x;
    }
    return a.y - b.y;
  });

  const compacted = [];
  const grid = [];

  sortedLayout.forEach(item => {
    let placed = false;
    
    // Try to place the item starting from the top
    for (let y = 0; !placed; y++) {
      for (let x = 0; x <= cols - item.w && !placed; x++) {
        // Check if this position is available
        let canPlace = true;
        for (let checkY = y; checkY < y + item.h && canPlace; checkY++) {
          for (let checkX = x; checkX < x + item.w && canPlace; checkX++) {
            if (grid[checkY] && grid[checkY][checkX]) {
              canPlace = false;
            }
          }
        }
        
        if (canPlace) {
          // Place the item
          for (let fillY = y; fillY < y + item.h; fillY++) {
            if (!grid[fillY]) grid[fillY] = [];
            for (let fillX = x; fillX < x + item.w; fillX++) {
              grid[fillY][fillX] = true;
            }
          }
          
          compacted.push({
            ...item,
            x,
            y
          });
          placed = true;
        }
      }
    }
  });

  return compacted;
};

const theme = createTheme({
  components: {
    MuiButton: {
      styleOverrides: {
        outlined: {
          '&:hover': {
            backgroundColor: '#fff'
          }
        }
      }
    }
  }
});


export default function Dashboard({ userData }) {
  const navigate = useNavigate();
  const { apiConfig } = coreConfig;
  const [data, setData] = useState([]);
  const [layoutState, setLayoutState] = useState([]);
  const [isCompacting, setIsCompacting] = useState(false);

  // Add this computed property for the compacted layout
  const compactedLayout = useMemo(
    () => layoutState.length && compactLayout(layoutState, 3) || [],
    [layoutState]
  );
  const fetchData = async () => {
    try {
      const response = await axiosBase({
        api: apiConfig.endpoints.dashboard,
        method: apiConfig.reqMethod.POST,
        body: JSON.stringify({ where: [{ field: 'ShowOnDashboard', operator: 'equals', type: 'boolean', value: true }], sort: "ModelName ASC" }),
        navigate
      });
      if (!response.data.success) {
        return;
      }
      setData(response.data.records);
      let savedLayout = localStorage.getItem("dashboardlayout") || userData.userDetails.user.DashboardPreference;
      
      if (savedLayout) {
        savedLayout = JSON.parse(savedLayout);
        // Apply compacting to saved layout
        setLayoutState(compactLayout(savedLayout, 3));
      } else {
        const initialLayout = response.data.records.map((item, index) => ({
          i: item.EquipmentId.toString(),
          x: index % 3,
          y: Math.floor(index / 3),
          w: 1,
          h: 1
        }));
        setLayoutState(initialLayout);
      }
    } catch (error) {
      logger.error({ error, message: "Error fetching dashboard data" }); 
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDragStart = () => {
    setIsCompacting(true);
  };

  const handleDragStop = async (layout) => {
    setIsCompacting(false);
    
    // Compact the layout after drag
    const compacted = compactLayout(layout, 3);
    
    // Check if layout has actually changed
    if(!hasLayoutChanged({ updatedLayout: compacted, layoutState })) {
      return;
    }

    try {
      // Save layout to user preferences
      await axiosBase({
        api: apiConfig.endpoints.user + '/' + userData.userDetails.id,
        method: apiConfig.reqMethod.PUT,
        body: {
          DashboardPreference: JSON.stringify(compacted)
        }
      });
      
      // Save to localStorage and update state
      localStorage.setItem("dashboardlayout", JSON.stringify(compacted));
      setLayoutState(compacted);
    } catch (error) {
      logger.error({ error, message: "Error saving dashboard layout" });
    }
  };

  const hasLayoutChanged = ({ updatedLayout = [], layoutState = [] }) => {
    const layoutStateMap = new Map(layoutState.map(item => [item.i, item]));

    for(const updatedItem of updatedLayout) {
      const originalItem = layoutStateMap.get(updatedItem.i);

      if (!originalItem) {
        return true;
      }

      const keysToCompare = ["x", "y", "w", "h"]; // need to check only these keys

      if (keysToCompare.some(key => updatedItem[key] !== originalItem[key])) {
        return true;
      }
    };

    return false;
  };
  const layoutArray = Array.isArray(layoutState) ? layoutState : [];
  return (
    <ThemeProvider theme={theme}>
      <Stack>
        <Grid size={12} sx={{ marginBottom: '10px', marginTop: "10px" }}>
          <Box display="flex" alignItems="center" justifyContent='space-between' sx={{
            backgroundColor: "#fff",
            borderRadius: '5px',
            padding: '10px'
          }}>
            <Typography variant="h5" sx={{ color: 'text.secondary' }}>Dashboard</Typography>
          </Box>
        </Grid>

        <Grid size={12} display='flex' justifyContent='center'>
          <Button variant="outlined" sx={{ backgroundColor: '#fff', width: '20vw', textTransform: 'none', fontSize: '1rem' }} onClick={() => navigate("/scopescreen")}>
            View All Master Scopes
          </Button>
        </Grid>

        <Grid size={12} sx={{ marginTop: "0.5%", marginBottom: "1%" }}>
        {!data.length && <CircularProgress style={{ display: "flex", margin: "auto"  }} /> }
          <ResponsiveGridLayout
            className="layout"
            layout={isCompacting ? layoutState : compactedLayout}
            breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480 }}
            cols={{ lg: 3, md: 2, sm: 1, xs: 1 }}
            rowHeight={240}
            isResizable={false}
            resizeHandles={["s"]}
            isDraggable={true}
            onLayoutChange={setLayoutState}
            onDragStart={handleDragStart}
            onDragStop={handleDragStop}
          >
            {data.map((item) => {
              // Find the corresponding layout item for this equipment
              const layoutItem = layoutArray.find(layout => layout.i === item.EquipmentId.toString()) || {
                i: item.EquipmentId.toString(),
                x: 0,
                y: 0,
                w: 1,
                h: 1
              };
              
              return (
                <Box key={item.EquipmentId} data-grid={layoutItem}>
                  <EquipmentCard item={item} navigate={navigate} />
                </Box>
              );
            })}
          </ResponsiveGridLayout>
        </Grid>
      </Stack>
    </ThemeProvider>
  );
}
